// =============================================================================
// PEDRA — src/lib/pdf.ts
// Escritor de PDF minimo, sem dependencia: paginas, retangulos, linhas,
// circulos, texto, recorte e hachura em padrao lado a lado (tiling pattern).
//
// POR QUE nao usar a impressao do navegador: "Salvar como PDF" pela caixa de
// impressao e uma via-crucis no celular (no iPhone nem existe um botao para
// isso). Aqui o arquivo nasce pronto e o botao so baixa.
//
// CONVENCOES:
//   - Coordenadas em PONTOS (1/72 pol), origem no canto SUPERIOR esquerdo e
//     Y crescendo para BAIXO — igual ao SVG e ao DOM. A inversao para o
//     sistema do PDF (origem embaixo) acontece uma unica vez, no inicio de
//     cada pagina; o texto desfaz a inversao na propria matriz de texto.
//   - Fontes: as tres padrao do PDF (Helvetica, Helvetica-Bold e Courier),
//     que todo leitor tem. Nada e embutido, entao o arquivo fica pequeno. A
//     codificacao e a WinAnsi: cobre todo o portugues (acentos, cedilha, ×, °).
//   - O conteudo e montado como string em que todo caractere cabe num byte
//     (latin-1). Assim o tamanho da string E o tamanho em bytes, e a tabela
//     xref sai com os deslocamentos certos sem precisar de TextEncoder.
// =============================================================================

// -----------------------------------------------------------------------------
// TIPOS
// -----------------------------------------------------------------------------

export type PdfFont = 'sans' | 'sansBold' | 'mono';

/** Cor RGB com canais de 0 a 1. */
export type PdfColor = readonly [number, number, number];

export type PdfAlign = 'left' | 'center' | 'right';

/**
 * Onde fica o `y` do texto: na linha de base (`alphabetic`), no meio da
 * altura das maiusculas (`middle`) ou no topo delas (`top`).
 */
export type PdfBaseline = 'alphabetic' | 'middle' | 'top';

export interface PdfTextOptions {
  font?: PdfFont;
  size?: number;
  align?: PdfAlign;
  baseline?: PdfBaseline;
  color?: PdfColor;
}

/** Matriz afim [a b c d e f], a mesma do SVG e do operador `cm`. */
export type PdfMatrix = readonly [number, number, number, number, number, number];

export const IDENTITY_MATRIX: PdfMatrix = [1, 0, 0, 1, 0, 0];

/** A4 em pontos. */
export const A4_PORTRAIT = { width: 595.28, height: 841.89 } as const;
export const A4_LANDSCAPE = { width: 841.89, height: 595.28 } as const;

// -----------------------------------------------------------------------------
// FONTES — METRICAS E CODIFICACAO
// -----------------------------------------------------------------------------

/** Nome PostScript e apelido de recurso de cada fonte. */
const FONT_DEFS: Readonly<Record<PdfFont, { base: string; res: string }>> = {
  sans: { base: 'Helvetica', res: 'F1' },
  sansBold: { base: 'Helvetica-Bold', res: 'F2' },
  mono: { base: 'Courier', res: 'F3' },
};

/**
 * Larguras (1/1000 em) dos codigos WinAnsi 32..255, tiradas dos AFM da Adobe.
 * Zero = codigo sem glifo na WinAnsi (nunca e emitido: `encodeWinAnsi` troca
 * por "?").
 */
const HELVETICA_WIDTHS: readonly number[] = [
  // 32..126
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
  278, 278, 584, 584, 584, 556, 1015,
  667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667,
  778, 722, 667, 611, 722, 667, 944, 667, 667, 611,
  278, 278, 278, 469, 556, 333,
  556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556,
  556, 333, 500, 278, 556, 500, 722, 500, 500, 500,
  334, 260, 334, 584,
  // 127
  0,
  // 128..159
  556, 0, 222, 556, 333, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0,
  0, 222, 222, 333, 333, 350, 556, 1000, 333, 1000, 500, 333, 944, 0, 500, 667,
  // 160..255
  278, 333, 556, 556, 556, 556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333,
  400, 584, 333, 333, 333, 556, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  667, 667, 667, 667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278,
  722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  556, 556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 584, 611, 556, 556, 556, 556, 500, 556, 500,
];

const HELVETICA_BOLD_WIDTHS: readonly number[] = [
  // 32..126
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
  333, 333, 584, 584, 584, 611, 975,
  722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778, 667,
  778, 722, 667, 611, 722, 667, 944, 667, 667, 611,
  333, 278, 333, 584, 556, 333,
  556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, 611,
  611, 389, 556, 333, 611, 556, 778, 556, 556, 500,
  389, 280, 389, 584,
  // 127
  0,
  // 128..159
  556, 0, 278, 556, 500, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0,
  0, 278, 278, 500, 500, 350, 556, 1000, 333, 1000, 556, 333, 944, 0, 500, 667,
  // 160..255
  278, 333, 556, 556, 556, 556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333,
  400, 584, 333, 333, 333, 611, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  722, 722, 722, 722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278,
  722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  556, 556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278,
  611, 611, 611, 611, 611, 611, 611, 584, 611, 611, 611, 611, 611, 556, 611, 556,
];

/** Courier e monoespacada: todo glifo tem 600. */
const COURIER_WIDTH = 600;

/** Caracteres Unicode que a WinAnsi guarda na faixa 0x80..0x9F. */
const WIN_ANSI_EXTRA: Readonly<Record<number, number>> = {
  0x20ac: 0x80, // €
  0x201a: 0x82, // ‚
  0x0192: 0x83, // ƒ
  0x201e: 0x84, // „
  0x2026: 0x85, // …
  0x2020: 0x86, // †
  0x2021: 0x87, // ‡
  0x02c6: 0x88, // ˆ
  0x2030: 0x89, // ‰
  0x0160: 0x8a, // Š
  0x2039: 0x8b, // ‹
  0x0152: 0x8c, // Œ
  0x017d: 0x8e, // Ž
  0x2018: 0x91, // '
  0x2019: 0x92, // '
  0x201c: 0x93, // "
  0x201d: 0x94, // "
  0x2022: 0x95, // •
  0x2013: 0x96, // –
  0x2014: 0x97, // —
  0x02dc: 0x98, // ˜
  0x2122: 0x99, // ™
  0x0161: 0x9a, // š
  0x203a: 0x9b, // ›
  0x0153: 0x9c, // œ
  0x017e: 0x9e, // ž
  0x0178: 0x9f, // Ÿ
};

/** Trocas para caracteres que a WinAnsi nao tem, mas tem um equivalente. */
const WIN_ANSI_FALLBACK: Readonly<Record<string, string>> = {
  '→': '->',
  '←': '<-',
  '⇒': '=>',
  '≤': '<=',
  '≥': '>=',
  '≠': '!=',
  '−': '-',
  '‐': '-',
  '‑': '-',
  '\u00a0': ' ',
  '\u2009': ' ',
  '\u202f': ' ',
  '\t': ' ',
  '\n': ' ',
  '\r': ' ',
};

/**
 * Converte texto Unicode em string de bytes WinAnsi (um char = um byte).
 * O que nao tiver equivalente vira "?" — nunca um byte invalido.
 */
export function encodeWinAnsi(text: string): string {
  const src = (text ?? '').normalize('NFC');
  let out = '';
  for (const ch of src) {
    const fallback = WIN_ANSI_FALLBACK[ch];
    if (fallback !== undefined) {
      out += fallback;
      continue;
    }
    const cp = ch.codePointAt(0) ?? 0x3f;
    if ((cp >= 0x20 && cp <= 0x7e) || (cp >= 0xa0 && cp <= 0xff)) {
      out += String.fromCharCode(cp);
      continue;
    }
    const extra = WIN_ANSI_EXTRA[cp];
    out += String.fromCharCode(extra ?? 0x3f);
  }
  return out;
}

/** true quando o texto tem ao menos um caractere que a WinAnsi representa. */
export function isEncodable(text: string): boolean {
  for (const ch of (text ?? '').normalize('NFC')) {
    if (WIN_ANSI_FALLBACK[ch] !== undefined) return true;
    const cp = ch.codePointAt(0) ?? 0;
    if ((cp > 0x20 && cp <= 0x7e) || (cp >= 0xa1 && cp <= 0xff)) return true;
    if (WIN_ANSI_EXTRA[cp] !== undefined) return true;
  }
  return false;
}

/** Largura de uma string JA codificada, em unidades de 1/1000 em. */
function encodedWidth(encoded: string, font: PdfFont): number {
  if (font === 'mono') return encoded.length * COURIER_WIDTH;
  const table = font === 'sansBold' ? HELVETICA_BOLD_WIDTHS : HELVETICA_WIDTHS;
  let w = 0;
  for (let i = 0; i < encoded.length; i++) {
    const code = encoded.charCodeAt(i);
    w += code >= 32 ? (table[code - 32] ?? 556) || 556 : 0;
  }
  return w;
}

/** Largura do texto em pontos, no corpo `size`. */
export function measureText(text: string, font: PdfFont, size: number): number {
  return (encodedWidth(encodeWinAnsi(text), font) * size) / 1000;
}

/**
 * Corta o texto para caber em `maxWidth`, com reticencias. Devolve '' quando
 * nem a reticencia cabe.
 */
export function fitTextWidth(text: string, maxWidth: number, font: PdfFont, size: number): string {
  if (measureText(text, font, size) <= maxWidth) return text;
  const chars = [...text];
  while (chars.length > 0) {
    chars.pop();
    const candidate = `${chars.join('').trimEnd()}…`;
    if (measureText(candidate, font, size) <= maxWidth) return candidate;
  }
  return '';
}

/** Altura das maiusculas (cap height) em fracao do corpo — AFM de cada fonte. */
function capHeight(font: PdfFont): number {
  return font === 'mono' ? 0.571 : 0.718;
}

// -----------------------------------------------------------------------------
// NUMEROS, STRINGS E CORES
// -----------------------------------------------------------------------------

/** Numero enxuto para o fluxo de conteudo: ate 3 casas, sem zero a direita. */
export function num(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const r = Math.round(n * 1000) / 1000;
  if (Object.is(r, -0) || r === 0) return '0';
  return String(r);
}

/** String literal do PDF, a partir de bytes WinAnsi ja codificados. */
function pdfString(encoded: string): string {
  let out = '(';
  for (let i = 0; i < encoded.length; i++) {
    const ch = encoded[i] as string;
    const code = encoded.charCodeAt(i);
    if (ch === '\\' || ch === '(' || ch === ')') out += `\\${ch}`;
    else if (code < 0x20 || code > 0x7e) out += `\\${code.toString(8).padStart(3, '0')}`;
    else out += ch;
  }
  return `${out})`;
}

/**
 * String de METADADO (titulo, autor...). Ela nao passa por fonte nenhuma, e
 * sim pela PDFDocEncoding, que difere da WinAnsi justamente em —, ", ' e cia.
 * Em UTF-16BE (com BOM) qualquer leitor mostra o texto exato.
 */
function pdfTextString(text: string): string {
  let hex = 'FEFF';
  for (let i = 0; i < text.length; i++) {
    hex += text.charCodeAt(i).toString(16).toUpperCase().padStart(4, '0');
  }
  return `<${hex}>`;
}

/** Cores nomeadas que aparecem de fato no app (o resto cai no cinza). */
const NAMED_COLORS: Readonly<Record<string, PdfColor>> = {
  black: [0, 0, 0],
  white: [1, 1, 1],
  gray: [0.5, 0.5, 0.5],
  grey: [0.5, 0.5, 0.5],
  red: [1, 0, 0],
  green: [0, 0.5, 0],
  blue: [0, 0, 1],
};

/**
 * Le uma cor CSS (#rgb, #rrggbb, #rrggbbaa, rgb(), rgba(), hsl() e alguns
 * nomes). `null` para "none", "transparent" ou texto irreconhecivel.
 */
export function parseColor(raw: string | null | undefined): PdfColor | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  if (s === '' || s === 'none' || s === 'transparent') return null;

  const hex = /^#([0-9a-f]{3,8})$/.exec(s);
  if (hex) {
    let h = hex[1] as string;
    if (h.length === 3 || h.length === 4) h = [...h.slice(0, 3)].map((c) => c + c).join('');
    if (h.length !== 6 && h.length !== 8) return null;
    return [
      parseInt(h.slice(0, 2), 16) / 255,
      parseInt(h.slice(2, 4), 16) / 255,
      parseInt(h.slice(4, 6), 16) / 255,
    ];
  }

  const rgb = /^rgba?\(([^)]+)\)$/.exec(s);
  if (rgb) {
    const parts = (rgb[1] as string).split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const channel = (p: string): number =>
      p.endsWith('%') ? (parseFloat(p) / 100) * 255 : parseFloat(p);
    const [r, g, b] = parts.slice(0, 3).map(channel) as [number, number, number];
    if (![r, g, b].every(Number.isFinite)) return null;
    return [clamp01(r / 255), clamp01(g / 255), clamp01(b / 255)];
  }

  const hsl = /^hsla?\(([^)]+)\)$/.exec(s);
  if (hsl) {
    const parts = (hsl[1] as string).split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const h = (((parseFloat(parts[0] as string) % 360) + 360) % 360) / 360;
    const sat = clamp01(parseFloat(parts[1] as string) / 100);
    const light = clamp01(parseFloat(parts[2] as string) / 100);
    if (![h, sat, light].every(Number.isFinite)) return null;
    return hslToRgb(h, sat, light);
  }

  return NAMED_COLORS[s] ?? null;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function hslToRgb(h: number, s: number, l: number): PdfColor {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [hue(h + 1 / 3), hue(h), hue(h - 1 / 3)];
}

function colorOps(c: PdfColor, stroke: boolean): string {
  return `${num(c[0])} ${num(c[1])} ${num(c[2])} ${stroke ? 'RG' : 'rg'}`;
}

// -----------------------------------------------------------------------------
// MATRIZES
// -----------------------------------------------------------------------------

/** `a` depois `b`: aplica primeiro `b`, depois `a` (mesma ordem do `cm`). */
export function multiply(a: PdfMatrix, b: PdfMatrix): PdfMatrix {
  return [
    b[0] * a[0] + b[1] * a[2],
    b[0] * a[1] + b[1] * a[3],
    b[2] * a[0] + b[3] * a[2],
    b[2] * a[1] + b[3] * a[3],
    b[4] * a[0] + b[5] * a[2] + a[4],
    b[4] * a[1] + b[5] * a[3] + a[5],
  ];
}

/** Fator de escala medio da matriz (raiz do determinante). */
export function matrixScale(m: PdfMatrix): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1;
}

// -----------------------------------------------------------------------------
// PADRAO LADO A LADO (hachura)
// -----------------------------------------------------------------------------

interface PatternDef {
  id: number;
  width: number;
  height: number;
  /** Matriz do espaco do padrao para o espaco PADRAO da pagina. */
  matrix: PdfMatrix;
  content: string;
}

// -----------------------------------------------------------------------------
// PAGINA
// -----------------------------------------------------------------------------

/** Base de todas as paginas: inverte o Y para a origem ficar no topo. */
function flipMatrix(height: number): PdfMatrix {
  return [1, 0, 0, -1, 0, height];
}

export class PdfPage {
  readonly width: number;
  readonly height: number;
  private readonly ops: string[] = [];
  private readonly doc: PdfDocument;
  /** Pilha espelhando `q`/`Q`: a matriz atual, para hachuras e espessuras. */
  private readonly stack: PdfMatrix[] = [];
  private ctm: PdfMatrix;

  constructor(doc: PdfDocument, width: number, height: number) {
    this.doc = doc;
    this.width = width;
    this.height = height;
    this.ctm = flipMatrix(height);
    this.ops.push(`1 0 0 -1 0 ${num(height)} cm`);
  }

  /** Matriz atual relativa ao espaco padrao do PDF (origem embaixo). */
  get matrix(): PdfMatrix {
    return this.ctm;
  }

  /** Fator de escala da matriz atual em relacao aos pontos da pagina. */
  get scale(): number {
    return matrixScale(this.ctm);
  }

  /** Fluxo de conteudo desta pagina (bytes latin-1). */
  content(): string {
    return `${this.ops.join('\n')}\n`;
  }

  // --- estado -----------------------------------------------------------------

  save(): this {
    this.stack.push(this.ctm);
    this.ops.push('q');
    return this;
  }

  restore(): this {
    const prev = this.stack.pop();
    if (prev === undefined) return this;
    this.ctm = prev;
    this.ops.push('Q');
    return this;
  }

  transform(m: PdfMatrix): this {
    if (m.every((v, i) => v === IDENTITY_MATRIX[i])) return this;
    this.ctm = multiply(this.ctm, m);
    this.ops.push(`${m.map(num).join(' ')} cm`);
    return this;
  }

  fillColor(c: PdfColor): this {
    this.ops.push(colorOps(c, false));
    return this;
  }

  strokeColor(c: PdfColor): this {
    this.ops.push(colorOps(c, true));
    return this;
  }

  /** Espessura em unidades do espaco ATUAL. */
  lineWidth(w: number): this {
    this.ops.push(`${num(Math.max(0, w))} w`);
    return this;
  }

  /** Tracejado em unidades do espaco atual; `[]` = linha continua. */
  dash(pattern: readonly number[], phase = 0): this {
    this.ops.push(`[${pattern.map(num).join(' ')}] ${num(phase)} d`);
    return this;
  }

  /** Ponta e juncao dos tracos: 0 = reto, 1 = redondo, 2 = quadrado. */
  lineCap(cap: 0 | 1 | 2): this {
    this.ops.push(`${cap} J`);
    return this;
  }

  // --- caminhos ---------------------------------------------------------------

  moveTo(x: number, y: number): this {
    this.ops.push(`${num(x)} ${num(y)} m`);
    return this;
  }

  lineTo(x: number, y: number): this {
    this.ops.push(`${num(x)} ${num(y)} l`);
    return this;
  }

  curveTo(x1: number, y1: number, x2: number, y2: number, x: number, y: number): this {
    this.ops.push(`${num(x1)} ${num(y1)} ${num(x2)} ${num(y2)} ${num(x)} ${num(y)} c`);
    return this;
  }

  closePath(): this {
    this.ops.push('h');
    return this;
  }

  rect(x: number, y: number, w: number, h: number): this {
    this.ops.push(`${num(x)} ${num(y)} ${num(w)} ${num(h)} re`);
    return this;
  }

  roundedRect(x: number, y: number, w: number, h: number, r: number): this {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    if (rr === 0) return this.rect(x, y, w, h);
    const k = rr * 0.5523;
    return this.moveTo(x + rr, y)
      .lineTo(x + w - rr, y)
      .curveTo(x + w - rr + k, y, x + w, y + rr - k, x + w, y + rr)
      .lineTo(x + w, y + h - rr)
      .curveTo(x + w, y + h - rr + k, x + w - rr + k, y + h, x + w - rr, y + h)
      .lineTo(x + rr, y + h)
      .curveTo(x + rr - k, y + h, x, y + h - rr + k, x, y + h - rr)
      .lineTo(x, y + rr)
      .curveTo(x, y + rr - k, x + rr - k, y, x + rr, y)
      .closePath();
  }

  circle(cx: number, cy: number, r: number): this {
    const k = r * 0.5523;
    return this.moveTo(cx + r, cy)
      .curveTo(cx + r, cy + k, cx + k, cy + r, cx, cy + r)
      .curveTo(cx - k, cy + r, cx - r, cy + k, cx - r, cy)
      .curveTo(cx - r, cy - k, cx - k, cy - r, cx, cy - r)
      .curveTo(cx + k, cy - r, cx + r, cy - k, cx + r, cy)
      .closePath();
  }

  fill(): this {
    this.ops.push('f');
    return this;
  }

  stroke(): this {
    this.ops.push('S');
    return this;
  }

  fillAndStroke(): this {
    this.ops.push('B');
    return this;
  }

  /** Usa o caminho atual como recorte (e descarta o desenho dele). */
  clip(): this {
    this.ops.push('W n');
    return this;
  }

  // --- hachura ----------------------------------------------------------------

  /**
   * Preenche o caminho atual com um padrao lado a lado. `draw` desenha UMA
   * celula (0..width x 0..height, no espaco atual) numa pagina provisoria.
   * O padrao fica preso ao espaco atual: escala e rotacao valem para ele.
   */
  fillPattern(width: number, height: number, draw: (cell: PdfPage) => void): this {
    const cell = new PdfPage(this.doc, 0, 0);
    // A celula nao herda a inversao de pagina: o padrao ja sai no espaco atual.
    cell.ops.length = 0;
    cell.ctm = IDENTITY_MATRIX;
    draw(cell);
    const name = this.doc.registerPattern(width, height, this.ctm, cell.ops.join('\n'));
    this.ops.push(`/Pattern cs /${name} scn f`);
    return this;
  }

  // --- texto ------------------------------------------------------------------

  /**
   * Escreve uma linha de texto. `x`/`y` seguem `align`/`baseline`, e a largura
   * vem das metricas da propria fonte do PDF — nao da fonte da tela.
   */
  text(value: string, x: number, y: number, options: PdfTextOptions = {}): this {
    const font = options.font ?? 'sans';
    const size = options.size ?? 10;
    const encoded = encodeWinAnsi(value);
    if (encoded === '') return this;

    const width = (encodedWidth(encoded, font) * size) / 1000;
    const align = options.align ?? 'left';
    const left = align === 'center' ? x - width / 2 : align === 'right' ? x - width : x;

    const baseline = options.baseline ?? 'alphabetic';
    const cap = capHeight(font) * size;
    const base = baseline === 'middle' ? y + cap / 2 : baseline === 'top' ? y + cap : y;

    const res = FONT_DEFS[font].res;
    const color = options.color ? `${colorOps(options.color, false)} ` : '';
    this.ops.push(
      `BT ${color}/${res} ${num(size)} Tf 1 0 0 -1 ${num(left)} ${num(base)} Tm ${pdfString(encoded)} Tj ET`,
    );
    return this;
  }
}

// -----------------------------------------------------------------------------
// DOCUMENTO
// -----------------------------------------------------------------------------

export interface PdfInfo {
  title?: string;
  author?: string;
  subject?: string;
}

/** Data no formato do PDF: D:AAAAMMDDHHmmSS. */
function pdfDate(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `D:${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(
    d.getMinutes(),
  )}${p(d.getSeconds())}`;
}

export class PdfDocument {
  private readonly pages: PdfPage[] = [];
  private readonly patterns: PatternDef[] = [];
  private readonly info: PdfInfo;

  constructor(info: PdfInfo = {}) {
    this.info = info;
  }

  addPage(width: number = A4_PORTRAIT.width, height: number = A4_PORTRAIT.height): PdfPage {
    const page = new PdfPage(this, width, height);
    this.pages.push(page);
    return page;
  }

  get pageCount(): number {
    return this.pages.length;
  }

  /** Paginas na ordem em que foram criadas (para rodape "pagina N de M"). */
  get allPages(): readonly PdfPage[] {
    return this.pages;
  }

  /** @internal Usado por `PdfPage.fillPattern`. */
  registerPattern(width: number, height: number, matrix: PdfMatrix, content: string): string {
    const id = this.patterns.length + 1;
    this.patterns.push({ id, width, height, matrix, content: `${content}\n` });
    return `P${id}`;
  }

  /** Serializa o documento inteiro como string de bytes latin-1. */
  toBinaryString(now: Date = new Date()): string {
    // Numeracao fixa dos objetos:
    //   1 catalogo · 2 arvore de paginas · 3 info · 4..6 fontes
    //   depois os padroes e, por fim, pagina + conteudo de cada pagina.
    const fontKeys = Object.keys(FONT_DEFS) as PdfFont[];
    const firstFont = 4;
    const firstPattern = firstFont + fontKeys.length;
    const firstPage = firstPattern + this.patterns.length;

    const objects: string[] = [];
    const set = (id: number, body: string): void => {
      objects[id] = body;
    };

    const pageIds = this.pages.map((_, i) => firstPage + i * 2);
    set(1, '<< /Type /Catalog /Pages 2 0 R >>');
    set(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${this.pages.length} >>`);

    const infoParts: string[] = [`/Producer ${pdfTextString('Pedra — plano de corte')}`];
    if (this.info.title) infoParts.push(`/Title ${pdfTextString(this.info.title)}`);
    if (this.info.author) infoParts.push(`/Author ${pdfTextString(this.info.author)}`);
    if (this.info.subject) infoParts.push(`/Subject ${pdfTextString(this.info.subject)}`);
    infoParts.push(`/CreationDate (${pdfDate(now)})`);
    set(3, `<< ${infoParts.join(' ')} >>`);

    fontKeys.forEach((key, i) => {
      set(
        firstFont + i,
        `<< /Type /Font /Subtype /Type1 /BaseFont /${FONT_DEFS[key].base} /Encoding /WinAnsiEncoding >>`,
      );
    });

    this.patterns.forEach((p, i) => {
      set(
        firstPattern + i,
        `<< /Type /Pattern /PatternType 1 /PaintType 1 /TilingType 1 ` +
          `/BBox [0 0 ${num(p.width)} ${num(p.height)}] /XStep ${num(p.width)} /YStep ${num(p.height)} ` +
          `/Matrix [${p.matrix.map(num).join(' ')}] /Resources << >> /Length ${p.content.length} >>\n` +
          `stream\n${p.content}endstream`,
      );
    });

    const fontRes = fontKeys.map((key, i) => `/${FONT_DEFS[key].res} ${firstFont + i} 0 R`).join(' ');
    const patternRes = this.patterns.map((p, i) => `/P${p.id} ${firstPattern + i} 0 R`).join(' ');
    const resources =
      `<< /Font << ${fontRes} >>` + (patternRes !== '' ? ` /Pattern << ${patternRes} >>` : '') + ' >>';

    this.pages.forEach((page, i) => {
      const pageId = firstPage + i * 2;
      const contentId = pageId + 1;
      set(
        pageId,
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(page.width)} ${num(page.height)}] ` +
          `/Resources ${resources} /Contents ${contentId} 0 R >>`,
      );
      const stream = page.content();
      set(contentId, `<< /Length ${stream.length} >>\nstream\n${stream}endstream`);
    });

    // "%âãÏÓ" no cabecalho avisa aos programas que o arquivo e binario.
    let out = '%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n';
    const offsets: number[] = [];
    for (let id = 1; id < objects.length; id++) {
      offsets[id] = out.length;
      out += `${id} 0 obj\n${objects[id] ?? 'null'}\nendobj\n`;
    }

    const xrefAt = out.length;
    out += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
    for (let id = 1; id < objects.length; id++) {
      out += `${String(offsets[id] ?? 0).padStart(10, '0')} 00000 n \n`;
    }
    out += `trailer\n<< /Size ${objects.length} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`;
    return out;
  }

  toBytes(now?: Date): Uint8Array<ArrayBuffer> {
    const s = this.toBinaryString(now);
    const bytes = new Uint8Array(new ArrayBuffer(s.length));
    for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i) & 0xff;
    return bytes;
  }

  toBlob(now?: Date): Blob {
    return new Blob([this.toBytes(now)], { type: 'application/pdf' });
  }
}
